import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { aboutData } from "@/mocks/about";
import { siteInfo } from "@/mocks/profile";

const pageTitle = `关于我们 - ${siteInfo.name}`;
const pageDesc = `${aboutData.brandIntro} 了解我们的使命、发展历程与联系方式。`;

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "苍井樱官方资料站是什么？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "苍井樱官方资料站创立于2020年，是国内最早专注于苍井樱资讯的中文平台之一。我们致力于为广大华语粉丝提供最全面、最权威、最及时的苍井樱相关资讯。",
      },
    },
    {
      "@type": "Question",
      name: "网站的信息来源是否可靠？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "本站所有信息均经过严格核实，确保内容真实准确。我们引用的资料来源包括官方公告、权威媒体报道以及苍井樱本人社交平台的公开信息。",
      },
    },
    {
      "@type": "Question",
      name: "如何联系资料站团队？",
      acceptedAnswer: {
        "@type": "Answer",
        text: `您可以通过以下方式联系我们：邮箱 ${aboutData.contactInfo.email}，微博 @苍井樱官方资料站，微信公众号 SakuraInfo2020。`,
      },
    },
    {
      "@type": "Question",
      name: "这个网站是苍井樱的官方网站吗？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "本站为粉丝自建的非官方资讯平台，所有内容仅供粉丝交流参考。我们与苍井樱及其所属事务所无直接关联。",
      },
    },
  ],
};

export default function AboutPage() {
  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`关于我们,苍井樱资料站,${siteInfo.keywords}`} />
      <link rel="canonical" href={`${siteInfo.url}/about`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:url" content={`${siteInfo.url}/about`} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: pageTitle,
          description: pageDesc,
          url: `${siteInfo.url}/about`,
          mainEntity: {
            "@type": "Organization",
            name: aboutData.brandName,
            description: aboutData.brandIntro,
            foundingDate: "2020",
            contactPoint: {
              "@type": "ContactPoint",
              email: aboutData.contactInfo.email,
              contactType: "customer service",
              availableLanguage: ["Chinese"],
            },
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqJsonLd)}
      </script>

      <div className="min-h-screen bg-background-50 pt-16">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "关于我们" },
          ]}
        />

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-xs tracking-[0.2em] text-primary-500 mb-3 font-medium">
              ABOUT US
            </p>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground-950 mb-6">
              关于{aboutData.brandName}
            </h1>
            <p className="text-foreground-600 leading-relaxed max-w-3xl mx-auto text-sm md:text-base">
              {aboutData.brandIntro}
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="bg-background-50 rounded-2xl p-8 md:p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-quill-pen-line text-2xl text-primary-500"></i>
              </div>
              <h2 className="text-2xl font-semibold text-foreground-950 mb-4">我们的使命</h2>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base max-w-2xl mx-auto">
                {aboutData.mission}
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 mb-10 text-center">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutData.values.map((value) => (
                <div
                  key={value.title}
                  className="bg-background-100 rounded-xl p-6 text-center hover:bg-background-100/80 transition-all duration-300"
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                    <i className={`${value.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Development History */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2"></div>
              <div className="flex flex-col gap-8">
                {aboutData.history.map((item, index) => (
                  <div key={item.year} className="flex items-start gap-6 flex-row">
                    <div className="hidden md:flex w-1/2 justify-end">
                      {index % 2 === 0 && (
                        <div className="bg-background-50 rounded-xl p-5 max-w-xs">
                          <span className="text-2xl font-bold text-primary-500">{item.year}</span>
                          <p className="text-sm text-foreground-600 mt-1">{item.event}</p>
                        </div>
                      )}
                    </div>
                    <div className="relative z-10 flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-background-50"></div>
                      </div>
                    </div>
                    <div className="flex-1 md:w-1/2">
                      <div className="bg-background-50 rounded-xl p-5 max-w-xs block">
                        <span className="text-2xl font-bold text-primary-500">{item.year}</span>
                        <p className="text-sm text-foreground-600 mt-1">{item.event}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                    {stat.value}
                  </p>
                  <p className="text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 mb-10 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutData.testimonials.map((t) => (
                <div key={t.name} className="bg-background-50 rounded-xl p-6 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1">「{t.content}」</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 mb-10 text-center">
              常见问题
            </h2>
            <div className="flex flex-col gap-4">
              {faqJsonLd.mainEntity.map((faq, index) => (
                <details key={index} className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100/80 transition-colors">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.name}</span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.acceptedAnswer.text}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 mb-10 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-background-50 rounded-xl p-6">
                <h3 className="text-base font-semibold text-foreground-950 mb-4">联系方式</h3>
                <div className="flex flex-col gap-4">
                  {[
                    { icon: "ri-mail-line", label: "邮箱", value: aboutData.contactInfo.email },
                    { icon: "ri-weibo-line", label: "微博", value: aboutData.contactInfo.weibo },
                    { icon: "ri-wechat-line", label: "微信", value: aboutData.contactInfo.wechat },
                    { icon: "ri-map-pin-line", label: "地址", value: aboutData.contactInfo.address },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-primary-500`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">{item.label}</p>
                        <p className="text-sm text-foreground-800">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6">
                <h3 className="text-base font-semibold text-foreground-950 mb-4">发送消息</h3>
                <form
                  id="contact-form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d9q5kqvnahb7382ov6jg"
                  method="POST"
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);
                    const honeypot = formData.get("website_alt");
                    if (honeypot && String(honeypot).trim() !== "") {
                      alert("消息已发送，感谢您的来信！");
                      form.reset();
                      return;
                    }
                    fetch(form.action, {
                      method: "POST",
                      body: new URLSearchParams(formData as unknown as Record<string, string>),
                      headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    })
                      .then((res) => res.text())
                      .then((text) => {
                        let parsed: Record<string, unknown> = {};
                        try { parsed = JSON.parse(text); } catch { /* ignore */ }
                        if (parsed?.code === "OK") {
                          alert("消息已发送成功，感谢您的来信！");
                          form.reset();
                        } else {
                          const meta = parsed?.meta as Record<string, string> | undefined;
                          const msg = meta?.message || meta?.detail || "发送失败，请稍后重试";
                          alert(msg);
                        }
                      })
                      .catch(() => alert("发送失败，请稍后重试"));
                  }}
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1">留言</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                  />
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                  >
                    发送消息
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="w-full px-4 md:px-6 py-10 bg-background-200/50">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs text-foreground-500 leading-relaxed">
              本站为非官方粉丝资讯平台，与苍井樱及其所属事务所无直接关联。所有内容均来自公开渠道，
              仅供粉丝交流参考。如有任何问题或建议，欢迎通过上方联系方式与我们取得联系。
            </p>
          </div>
        </section>
      </div>
    </>
  );
}